<script setup lang="ts">
import RelatedGames from '../../tournament/RelatedGames.vue';
import MissionReward from '../MissionReward.vue';
import { useMissionCard } from '~/composables/useMissionCard';

const props = defineProps<{
  mission: TMissionOrBadge;
  isFeatured?: boolean;
}>();

const emit = defineEmits<{
  missionOptedIn: [missionId: number];
}>();

const {
  mission,
  isFeatured,
  timer,
  missionProgress,
  progressBarColor,
  isModalVisible,
  isRelatedGamesModalVisible,
  isRewardModalVisible,
  formattedHintText,
  rewardData,
  isOptInRequired,
  isAlreadyParticipating,
  handleMissionClick,
} = useMissionCard(props, (missionId) => emit('missionOptedIn', missionId));
</script>

<template>
  <div :class="$style.cardWrapper">
    <div
      :class="[
        $style.missionCard,
        $style.participando,
        isFeatured ? $style.featuredMission : '',
      ]"
      draggable="false"
      @dragstart.prevent
    >
      <!-- Ribbon no topo centralizado -->
      <div
        v-if="mission.ribbon"
        :class="$style.ribbon"
      >
        <span :class="$style.ribbonText">{{ mission.ribbon }}</span>
      </div>

      <div :class="$style.cardContent">
        <!-- Header: Imagem + Info -->
        <div :class="$style.missionBlock">
          <div :class="$style.missionLeft">
            <SmarticoApostatudoSmarticoImage
              :alt="mission.name"
              :class="$style.image"
              :src="mission.image"
            />
          </div>
          <div :class="$style.missionRight">
            <div
              :class="$style.name"
              @click="handleMissionClick('reward')"
            >
              {{ mission.name }}
            </div>
            <div
              :class="$style.description"
              @click="handleMissionClick('reward')"
            >
              {{ mission.description }}
            </div>
          </div>
        </div>

        <!-- Duração -->
        <div
          v-if="isOptInRequired || isAlreadyParticipating"
          :class="$style.durationSection"
        >
          <div :class="$style.durationContent">
            <span :class="$style.durationLabel">DURAÇÃO</span>
            <div :class="$style.durationTimer">
              <NuxtIcon
                :class="$style.clockIcon"
                name="mission/duracao"
              />
              <span :class="$style.timerText">
                {{ Number(timer?.days) > 0 ? `${timer.days}d ` : ''
                }}{{ timer.hours }}:{{ timer.minutes }}:{{ timer.seconds }}
              </span>
            </div>
          </div>
          <!-- Recompensa dentro da seção de duração -->
          <div
            v-if="mission.reward"
            :class="$style.rewardSection"
            @click="handleMissionClick('reward')"
          >
            <NuxtIcon
              :class="$style.rewardIcon"
              :filled="true"
              name="mission/gift"
            />
            <span :class="$style.rewardText">{{ rewardData.reward }}</span>
          </div>
        </div>

        <!-- Progresso -->
        <div :class="$style.progressSection">
          <div :class="$style.progressBarContainer">
            <NuxtIcon
              :class="$style.energyIcon"
              :filled="true"
              name="mission/energy"
            />
            <div :class="$style.progressBar">
              <div
                :class="$style.progress"
                :style="{
                  width: `${missionProgress}%`,
                  backgroundColor: progressBarColor,
                }"
              ></div>
              <span :class="$style.progressText">{{ missionProgress }}%</span>
            </div>
            <NuxtIcon
              :class="$style.trophyIcon"
              :filled="true"
              name="mission/trophy"
            />
          </div>
        </div>

        <!-- Jogos Selecionados -->
        <div
          :class="$style.gamesSection"
          @click="handleMissionClick('relatedGames')"
        >
          <div :class="$style.gamesThumbsContainer">
            <div
              v-for="(related, index) in mission.related_games?.slice(0, 5)"
              :key="index"
              :class="$style.gameThumb"
            >
              <NuxtImg
                class="object-cover w-full h-full"
                :src="related?.game_public_meta?.image"
              />
            </div>
          </div>
          <span :class="$style.gamesText">Jogos Elegíveis</span>
        </div>
      </div>

      <!-- Modais -->
      <BaseModalMissionModal v-model="isModalVisible">
        <template #header>
          <NuxtIcon
            class="text-xl text-white"
            name="mission/info"
          />
          <p class="text-lg font-bold text-white">Termos e condições</p>
        </template>
        <div v-if="mission.hint_text">
          <p
            v-for="(line, index) in formattedHintText"
            :key="index"
            class="mb-2 text-white/50 font-normal text-[13px]"
          >
            {{ line }}
          </p>
        </div>
      </BaseModalMissionModal>

      <RelatedGames
        v-model:modal-visible="isRelatedGamesModalVisible"
        :is-loading="false"
        :items="mission.related_games"
        style="display: none"
        title="Jogos Elegíveis"
      />

      <MissionReward
        v-model:modal-visible="isRewardModalVisible"
        :mission="mission"
      />
    </div>
  </div>
</template>

<style module lang="postcss">
/* === CARD WRAPPER === */
.cardWrapper {
  width: 100%;
  min-width: 0;
  padding-top: 10px;
  /* Isola contexto de composição para evitar vazamento de blur/shadow */
  isolation: isolate;
  contain: layout style paint;
}

/* Dentro de swipers: largura fixa */
:global(.afiliado-swiper__items) .cardWrapper {
  width: 165px;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .cardWrapper {
    padding-top: 14px;
  }

  :global(.afiliado-swiper__items) .cardWrapper {
    width: 260px;
  }
}

/* === CARD BASE === */
.missionCard {
  @apply relative flex flex-col items-start backdrop-blur-[6px] rounded-[9px] overflow-visible select-none;
  width: 100%;
  height: 100%;
  padding: 9px;
  min-width: 0;
}
.missionCard::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(180deg, #1d2329 0%, #0062ff 100%);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
  z-index: -1;
}
@media (min-width: 640px) {
  /* Desktop */
  .missionCard {
    padding: 13px;
    border-radius: 13px;
    backdrop-filter: blur(9px);
  }
}
.participando {
  background: linear-gradient(
    180deg,
    rgba(2, 20, 66, 0.4) 0%,
    rgba(8, 56, 179, 0.4) 100%
  );
}
.cardContent {
  @apply flex flex-col justify-between w-full;
  flex: 1;
  gap: 5px;
}
@media (min-width: 640px) {
  .cardContent {
    gap: 7px;
  }
}

/* === RIBBON === */
/* Ribbon no topo centralizado */
.ribbon {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translate(-50%, -50%);
  z-index: 10;
  min-width: 80px;
  width: auto;
  max-width: calc(100% - 46px);
  height: 18px;
  padding: 0 8px;
  background: rgba(0, 98, 255, 0.6);
  backdrop-filter: blur(8px);
  border-radius: 4px;
  border: 1px solid rgba(0, 98, 255, 0.4);
  display: inline-flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

@media (min-width: 640px) {
  .ribbon {
    height: 22px;
    min-width: 96px;
    padding: 0 10px;
  }
}

.ribbonText {
  font-size: 9px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1;
}

@media (min-width: 640px) {
  .ribbonText {
    font-size: 11px;
  }
}

/* === HEADER: IMAGEM + INFO === */
.missionBlock {
  @apply flex items-stretch;
  gap: 6px;
}
@media (min-width: 640px) {
  .missionBlock {
    gap: 10px;
  }
}
.missionLeft {
  @apply relative flex-shrink-0;
}
.image {
  @apply rounded-lg overflow-hidden;
  width: 63px;
  height: 63px;
}
@media (min-width: 640px) {
  .image {
    width: 94px;
    height: 94px;
  }
}
.missionRight {
  @apply flex flex-col items-start justify-around flex-1 min-w-0;
  height: 100%;
}
.name {
  @apply w-full overflow-hidden font-bold text-white leading-tight cursor-pointer hover:brightness-75;
  font-size: 12px;
  line-height: 15px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  word-break: break-word;
}
@media (min-width: 640px) {
  .name {
    font-size: 19px;
    line-height: 22px;
  }
}
.description {
  @apply w-full overflow-hidden font-normal leading-tight cursor-pointer hover:brightness-75;
  font-size: 10px;
  line-height: 12px;
  color: #a1a1aa;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  word-break: break-word;
}
@media (min-width: 640px) {
  .description {
    font-size: 15px;
    line-height: 19px;
  }
}

/* === DURAÇÃO + PRÊMIO === */
.durationSection {
  @apply flex flex-col w-full rounded-[6px];
  background: rgba(255, 255, 255, 0.05);
  gap: 3px;
}
@media (min-width: 640px) {
  .durationSection {
    border-radius: 10px;
    gap: 5px;
  }
}
.durationContent {
  @apply flex items-center justify-center w-full;
  padding: 3px 0;
  gap: 6px;
}
@media (min-width: 640px) {
  .durationContent {
    padding: 5px 0;
    gap: 10px;
  }
}
.durationLabel {
  @apply font-normal;
  font-size: 6px;
  color: #a1a1aa;
}
@media (min-width: 640px) {
  .durationLabel {
    font-size: 10px;
  }
}
.durationTimer {
  @apply flex items-center;
  gap: 3px;
}
@media (min-width: 640px) {
  .durationTimer {
    gap: 5px;
  }
}
.clockIcon {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
}
@media (min-width: 640px) {
  .clockIcon {
    font-size: 15px;
  }
}
.timerText {
  @apply font-bold text-white;
  font-size: 9px;
  font-variant-numeric: tabular-nums;
}
@media (min-width: 640px) {
  .timerText {
    font-size: 14px;
  }
}

/* === RECOMPENSA === */
.rewardSection {
  @apply flex items-center justify-center w-full cursor-pointer hover:brightness-110 transition-all;
  background: rgba(8, 48, 179, 0.1);
  backdrop-filter: blur(6px);
  padding: 1px 10px;
  border-radius: 0 0 6px 6px;
  height: 24px;
  gap: 6px;
}
@media (min-width: 640px) {
  .rewardSection {
    padding: 2px 16px;
    border-radius: 0 0 10px 10px;
    height: 38px;
    gap: 10px;
    backdrop-filter: blur(10px);
  }
}
.rewardIcon {
  font-size: 12px;
}
@media (min-width: 640px) {
  .rewardIcon {
    font-size: 19px;
  }
}
.rewardText {
  @apply font-bold text-center whitespace-nowrap overflow-hidden text-ellipsis;
  font-size: 10px;
  color: #00c9ff;
  max-width: 100%;
}
@media (min-width: 640px) {
  .rewardText {
    font-size: 14px;
  }
}

/* === PROGRESSO === */
.progressSection {
  @apply w-full;
  padding: 0;
}
@media (min-width: 640px) {
  .progressSection {
    padding: 0;
  }
}
.progressBarContainer {
  @apply flex items-center justify-center w-full;
}
.energyIcon {
  font-size: 16px;
  margin-right: -8px;
  z-index: 10;
}
@media (min-width: 640px) {
  .energyIcon {
    font-size: 25px;
    margin-right: -12px;
  }
}
.trophyIcon {
  font-size: 18px;
  margin-left: -8px;
  z-index: 10;
}
@media (min-width: 640px) {
  .trophyIcon {
    font-size: 28px;
    margin-left: -12px;
  }
}
.progressBar {
  @apply flex-1 relative overflow-hidden;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 64px;
  height: 12px;
}
@media (min-width: 640px) {
  .progressBar {
    border-radius: 100px;
    height: 19px;
  }
}
.progress {
  @apply h-full transition-all duration-300;
  background: #0062ff;
  border-radius: inherit;
}

.progressText {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 7px;
  line-height: 6px;
  color: #fffffff2;
}

@media (min-width: 640px) {
  .progressText {
    font-size: 11px;
    line-height: 10px;
  }
}

/* === JOGOS SELECIONADOS === */
.gamesSection {
  @apply flex items-center justify-around w-full cursor-pointer transition-colors;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 700px;
  padding: 4px 6px;
}
.gamesSection:hover {
  background: rgba(255, 255, 255, 0.1);
}
@media (min-width: 640px) {
  .gamesSection {
    border-radius: 1044px;
    padding: 5px 8px;
  }
}
.gamesThumbsContainer {
  @apply flex items-center;
}
.gameThumb {
  @apply rounded-full overflow-hidden flex-shrink-0;
  width: 19px;
  height: 19px;
  margin-right: -12px;
}
.gameThumb:last-child {
  margin-right: 0;
}
@media (min-width: 640px) {
  .gameThumb {
    width: 28px;
    height: 28px;
    margin-right: -14px;
  }
}
.gamesText {
  @apply font-normal whitespace-nowrap;
  font-size: 8px;
  color: rgba(255, 255, 255, 0.5);
}
@media (min-width: 640px) {
  .gamesText {
    font-size: 13px;
  }
}
</style>
